/* requires:
polyfill.js
*/

// fetch contentful article to preview and render the content on the page
(async function () {
  const inlineHyperlinks = {
    [INLINES.HYPERLINK]: (node, children) => {
      const target = node.data.uri.includes('domain_name_replaced_during_build')
        ? ''
        : 'target="_blank" aria-label="link opens in new tab"';
      return `<a href="${node.data.uri}" ${target}>${children(node.content)}</a>`;
    },
    [INLINES.ASSET_HYPERLINK]: (node, children) => {
      const fields = node.data.target.fields;
      return `<a href="${fields.file.url}" target="_blank" aria-label="link opens in new tab">${children(node.content)}</a>`;
    },
    [INLINES.ENTRY_HYPERLINK]: (node, children) => {
      const fields = node.data.target.fields;
      return `<a href="/${fields.slug}/">${children(node.content)}</a>`;
    },
  };
  const renderOptions = {
    renderNode: {
      ...inlineHyperlinks,
      [BLOCKS.LIST_ITEM]: (node) => {
        const transformedChildren = documentToHtmlString(node, {
          renderNode: {
            [BLOCKS.PARAGRAPH]: (node, next) => `${next(node.content)}`,
            // [BLOCKS.LIST_ITEM]: (node, next) => `${next(node.content)}`, // this does not allow another list to be rendered correctly inside a list
            ...inlineHyperlinks,
          },
        });
        return `<li>${transformedChildren}</li>`;
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const fields = node.data.target.fields;
        if (fields.file.contentType.includes('image')) {
          return `<img class="img-fluid float-end ms-4 my-3 d-none d-sm-block"
              src="${`https://${fields.file.url}`}"
              height="${fields.file.details.image.height}"
              width="${fields.file.details.image.width}"
              alt="${fields.title}"
              style="width: 100%; max-width: 35%; height: auto;"
            />`;
        } else {
          return `<a href="${fields.file.url}" target="_blank" aria-label="link opens in new tab">${fields.title}</a>`;
        }
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const fields = node.data.target.fields;
        if (fields.image) {
          const imageWrap = (children, alwaysShown) => {
            const alignment = fields.alignment;
            const alignClass =
              alignment === 'Centered'
                ? 'mx-auto my-4'
                : alignment === 'Left'
                  ? 'float-start me-4 aligned'
                  : 'float-end ms-4 aligned';
            const displayClass = alwaysShown || alignment === 'Centered' ? 'd-block' : 'd-none d-sm-block';

            return `<div class="post-inline-img text-center my-3 ${displayClass} ${alignClass}"
                      style="width: 100%;"
                    >
                      ${children}
                    </div>`;
          };

          const imageEl = `<img
                      class="img-fluid rounded"
                      src="${`https:${fields.image.fields.file.url}`}"
                      height="${fields.image.fields.file.details.image.height}"
                      width="${fields.image.fields.file.details.image.width}"
                      alt="${fields.altText}"
                    />`;

          if (fields.linkTo) {
            const target = fields.linkTo.includes('domain_name_replaced_during_build')
              ? ''
              : 'target="_blank" aria-label="link opens in new tab"';
            const linkEl = `<a class="border-0" href="${fields.linkTo}" ${target}>${imageEl}</a>`;
            return imageWrap(linkEl, true);
          }
          if (!fields.linkTo) {
            return imageWrap(imageEl, false);
          }
        }
        return;
      },
    },
  };

  const transformToReadableTag = (tag) => {
    if (!tag || !tag.sys || !tag.sys.id) {
      return '';
    }
    return tag.sys.id
      .replace('jade', '')
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // split lower-to-upper
      .replace(/^./, (s) => s.toUpperCase());
  };

  const transformTagToKebabCase = (tag) => {
    if (!tag || !tag.sys || !tag.sys.id) {
      return '';
    }
    const topicUrlMap = {
      jadeFeatured: 'featured',
      jadeWater: 'water',
      jadeRenewableEnergy: 'renewable-energy',
      jadeNationalElectricalCode: 'national-electrical-code',
      jadeElectricalSafety: 'electrical-safety',
      jadeElectricalLicensing: 'electrical-licensing',
      jadeElectricalConceptsAndApplications: 'electrical-concepts',
      jadeCareersForElectricians: 'careers',
      jadeAlarmsAndSecurity: 'alarm',
      jadeAirConditioningAndRefrigerationSystems: 'air-conditioning-and-refrigeration-systems',
    };
    return (
      topicUrlMap[tag.sys?.id] ||
      tag.sys?.id
        .replace('jade', '')
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2') // split lower-to-upper with hyphen
        .toLowerCase()
    );
  };

  const getCleanTags = (tags) => {
    return tags.filter((tag) => tag.sys?.id !== 'jadeFeatured'); // jadeFeatured is specific to Jade's
  };

  const formatDate = (date) => {
    const postDate = new Date(date);
    const year = postDate.getFullYear();
    const month = postDate.getMonth();
    const day = postDate.getDate();
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return `${months[month]} ${day}, ${year}`;
  };

  //   <p>
  //   Published on {{ post.sys.createdAt | date }} by
  //   <a href="{{ post.fields.authorBioUrl | replace("https://www." + post.fields.domain + "/", "/") }}">{{ post.fields.authorName }}</a>
  //   {% if post.fields.editorName and post.fields.editorBioUrl %}
  //   | Edited by <a href="{{ post.fields.editorBioUrl | replace("https://www." + post.fields.domain + "/", "/") }}">{{ post.fields.editorName }}</a>
  //   {% endif %}
  // </p>

  //   {% if post.metadata.tags | getCleanTags | first | transformToReadableTag %}
  //   <p class="category mb-1"><a href="/articles/{{ post.metadata.tags | getCleanTags | first | transformTagToKebabCase }}/">{{ post.metadata.tags | getCleanTags | first | transformToReadableTag }}</a></p>
  // {% endif %}

  // article html template
  const articleHtml = (post) => {
    return `
      <h1>${post.fields.title}</h1>

      ${post.metadata.tags && transformToReadableTag(getCleanTags(post.metadata.tags)[0]) !== '' ? `<p class="category mb-1"><a href="/articles/${transformTagToKebabCase(getCleanTags(post.metadata.tags)[0])}/">${transformToReadableTag(getCleanTags(post.metadata.tags)[0])}</a></p>` : ''}

      <p>
        Published on ${formatDate(post.sys.createdAt)} by
        <a href="${post.fields.authorBioUrl.replace('https://www.' + post.fields.domain + '/', '/')}">${
          post.fields.authorName
        }</a>

        ${
          post.fields.editorName && post.fields.editorBioUrl
            ? `| Edited by <a href="${post.fields.editorBioUrl.replace('https://www.' + post.fields.domain + '/', '/')}">${
                post.fields.editorName
              }</a>`
            : ''
        }
      </p>
      ${
        post.fields.featuredImage
          ? `<img class="post-img d-none d-sm-block float-start me-3 rounded p-1 border" src="${post.fields.featuredImage.fields.file.url}" alt="${post.fields.title}" loading="lazy" width="300" height="auto" />`
          : ''
      }
      ${documentToHtmlString(post.fields.articleBody, renderOptions).replaceAll(`https://www.${post.fields.domain}/`, '/')}
    `;
  };

  const getQueryString = (name, url) => {
    if (!url) url = window.location.href;
    name = name.replace(/[\[\]]/g, '\\$&');
    let regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
      results = regex.exec(url);
    if (!results) return null;
    if (!results[2]) return '';
    return decodeURIComponent(results[2].replace(/\+/g, ' '));
  };

  const postId = getQueryString('postId');

  if (!postId) {
    return;
  } else {
    try {
      // this is calling the function app for this domain
      const response = await fetch(`/api/contentful/?postId=${postId}`);

      if (response.ok) {
        const article = await response.json();
        // console.log(article);

        // render article on the page
        if (article && article.sys.type !== 'Error') {
          document.querySelector('#article-preview').innerHTML = articleHtml(article);
        } else {
          document.querySelector('#article-preview').innerHTML =
            '<h1 class="h3 text-danger mt-4">Error showing the preview...</h1>';
        }
      } else {
        // Custom message for failed HTTP codes
        if (response.status === 404) throw new Error('404, Not found');
        if (response.status === 500) throw new Error('500, internal server error');
        // For any other server error
        throw new Error(response.status);
      }
    } catch (error) {
      console.error('Fetch', error);
    }
  }
})();
